import { computed, markRaw, nextTick, ref, type Ref } from 'vue';
import { useRouter } from 'vue-router';
import { defineStore, storeToRefs } from 'pinia';
import {
  createForm,
  createVectorMapLayerGroup,
  FormElement,
  MAP_PROJECTION_3857,
  MAP_PROJECTION_4326,
  mergeExtents,
  SelectableFormElement,
  transformExtent,
  useFormElement,
  useModals,
  useSelectableFormElement
} from '@common';
import { useNotifications } from '@common';
import { Permission, useAuthStore, useCropStore, type WizardStep } from '@shared';
import { useApi } from '../../../services';
import 'nerdamer/Algebra.js';
import 'nerdamer/Calculus.js';
import 'nerdamer/Solve.js';
import { getDemoOffer } from '../../parcel/utils/offer.utils';
import type { ParcelListFilter } from '../../parcel/models/parcel-list-filter.model';
import type {
  DecisionSupportWizardData,
  DecisionSupportWizardParcelData,
  SaveDecisionSupportWizardData
} from '../models/decision-support-wizard.model';
import { StatisticsKind } from '../../statistics';
import type { ParcelGroupModalConfig } from '../../parcel/models/parcel-group-modal.model';
import ParcelGroupModal from '../../parcel/components/parcel-group-modal/ParcelGroupModal.vue';
import type { Extent } from 'ol/extent';
import type { ParcelItem } from '../../parcel/models/parcel-item.model';
import { DecisionSupportSource } from '../enums';
import DecisionSupportWizardStartStep from '../components/wizard/DecisionSupportWizardStartStep.vue';
import DecisionSupportWizardSourceStep from '../components/wizard/DecisionSupportWizardSourceStep.vue';
import DecisionSupportWizardOverviewStep from '../components/wizard/DecisionSupportWizardOverviewStep.vue';
import { useDecisionSupportWizardMap } from '../composables/decision-support-wizard-map.composable';
import {
  DECISION_SUPPORT_WIZARD_APPLY_PRICES_FORM_CONFIG,
  DECISION_SUPPORT_WIZARD_PARCEL_FORM_CONFIG
} from '../config';
import { useInfoModal } from '../../../composables';

const MODULE_PERMISSION = Permission.MODULE_DECISION_SUPPORT;

export const useDecisionSupportWizardStore = defineStore('decisionSupportWizardStore', () => {
  const router = useRouter();
  const modals = useModals();
  const notifications = useNotifications();

  const parcelApi = useApi().parcel;
  const statisticsApi = useApi().statistics;
  const decisionSupportApi = useApi().decisionSupport;

  const authStore = useAuthStore();

  const { isDemoOfferVisible } = storeToRefs(authStore);

  const cropStore = useCropStore();
  const { crops } = storeToRefs(cropStore);

  const { openInfoModal } = useInfoModal(
    'maximap.statistics.decisionSupport.wizard.info',
    'decisionSupport.label.wizardInfoTitle'
  );

  const decisionSupportData: Ref<DecisionSupportWizardData> = ref({
    name: undefined,
    year: undefined,
    kind: StatisticsKind.DECISION_SUPPORT,
    parcels: [],
    metadata: {
      crop: undefined,
      parcels: []
    },
    status: undefined
  });

  const isCopy = ref(false);
  const needMetadataUpdate = ref(true);

  const cropNameByCode = computed(() =>
    decisionSupportData.value.metadata.crop ? getCropNameByCode(decisionSupportData.value.metadata.crop) : '-'
  );

  const allParcelsValid = computed(() => {
    return decisionSupportData.value.parcels.every((parcel) => parcel.form.valid && !parcel.blocked);
  });

  const composedDecisionSupportWizardMap = useDecisionSupportWizardMap();
  const { map, isZoneScrollIntoViewDisabled, addLayer, layers, clearLayers, removeLayer, zoomToExtent } =
    composedDecisionSupportWizardMap;

  const steps = ref<WizardStep[]>([
    {
      title: 'decisionSupport.wizard.step.start',
      component: markRaw(DecisionSupportWizardStartStep),
      valid: () => validateStartStep()
    },
    {
      title: 'decisionSupport.wizard.step.source',
      component: markRaw(DecisionSupportWizardSourceStep),
      valid: () => validateSourceStep(),
      onLoad: () => onSourceStepLoad()
    },
    {
      title: 'decisionSupport.wizard.step.overview',
      component: markRaw(DecisionSupportWizardOverviewStep)
    }
  ]);
  const currentStep = ref(1);
  const isFirstStep = computed(() => currentStep.value === 1);
  const isLastStep = computed(() => currentStep.value === steps.value.length);
  const isCurrentStepValid = computed(() => {
    const current = steps.value[currentStep.value - 1];
    if (typeof current.valid === 'function') {
      return current.valid();
    }
    return true;
  });

  const isNextStepLoading = ref(false);
  const isParcelsLoading = ref(false);
  const isLayerLoading = ref(false);
  const isSaving = ref(false);
  const isSaveValid = ref(false);
  const isUiDisabled = computed(
    () => isNextStepLoading.value || isParcelsLoading.value || isSaving.value || isLayerLoading.value
  );

  const applyForm = createForm(DECISION_SUPPORT_WIZARD_APPLY_PRICES_FORM_CONFIG);

  const getApplyForm = computed(() => applyForm);

  const { getChild } = useFormElement(getApplyForm.value.element);

  const upkeepCostField = getChild('upkeepCost') as FormElement;
  const cropPriceField = getChild('cropPrice') as FormElement;

  const { value: cropPriceValue } = useFormElement(cropPriceField);

  const applyUnkeepCost = (newValue: number) => {
    if (newValue) {
      upkeepCostField.value = undefined;

      decisionSupportData.value.parcels.forEach(async (parcel) => {
        const form = parcel.form;
        const { getChild: getParcelForm, validate } = useFormElement(form.element);
        const upkeepCostField = getParcelForm('upkeepCost') as FormElement;
        const { value: upkeepCostValue } = useFormElement(upkeepCostField);
        upkeepCostValue.value = newValue;
        await validate();
      });
    }
  };

  const applyCropPrice = (newValue: number) => {
    if (newValue) {
      cropPriceValue.value = undefined;

      decisionSupportData.value.parcels.forEach(async (parcel) => {
        const form = parcel.form;
        const { getChild: getParcelForm, validate } = useFormElement(form.element);
        const cropPriceField = getParcelForm('cropPrice') as FormElement;
        const { value: cropPriceValue } = useFormElement(cropPriceField);
        cropPriceValue.value = newValue;
        await validate();
      });
    }
  };

  const getCropNameByCode = (code: string) => {
    const crop = crops.value.find((c) => c.code === code);
    return crop ? crop.name : '';
  };

  const copyDecisionSupport = async (decisionSupportId: string) => {
    currentStep.value = 2;
    isCopy.value = true;

    const originalDecisionSupportData = await loadDecisionSupportById(decisionSupportId);

    decisionSupportData.value = {
      name: undefined,
      year: originalDecisionSupportData.year,
      kind: StatisticsKind.DECISION_SUPPORT,
      parcels: [],
      metadata: {
        crop: originalDecisionSupportData.metadata.crop,
        parcels: originalDecisionSupportData.metadata.parcels
      }
    };

    const decisionSupportWizardParcelItems: ParcelItem[] = originalDecisionSupportData.parcels.map(
      (parcel: DecisionSupportWizardParcelData) => ({
        id: parcel.id,
        name: parcel.name,
        area: parcel.area,
        bbox: parcel.bbox,
        blocked: parcel.blocked,
        svg: parcel.svg,
        settlement: '',
        farmName: '',
        demo: parcel.demo ?? false
      })
    );

    await copyDecisionSupportParcels(decisionSupportWizardParcelItems);
  };

  const copyDecisionSupportParcels = async (parcels: ParcelItem[]) => {
    for (const parcel of parcels) {
      await setupSelectedParcel(parcel);
      await addLayers();
      fitParcelsExtent();
    }
  };

  const resetWizard = () => {
    isCopy.value = false;
    currentStep.value = 1;
    decisionSupportData.value = {
      name: undefined,
      year: undefined,
      kind: StatisticsKind.DECISION_SUPPORT,
      parcels: [],
      metadata: {
        crop: undefined,
        parcels: []
      },
      status: undefined
    };
    clearLayers();
  };

  const getParcelListFilter = (): ParcelListFilter => {
    const crop = decisionSupportData.value.metadata.crop;

    return { cropCode: crop ? [crop] : undefined };
  };

  const loadOffers = async () => {
    const filter = getParcelListFilter();

    const demoOffer = isDemoOfferVisible.value ? await getDemoOffer(decisionSupportData.value.year, filter) : null;
    const offers = demoOffer ? [demoOffer] : [];

    const response = await parcelApi.getOffers(MODULE_PERMISSION, decisionSupportData.value.year, filter);
    offers.push(...response.data);

    return offers;
  };

  const loadParcelGroups = async () => {
    const response = await parcelApi.getParcelGroups(
      MODULE_PERMISSION,
      decisionSupportData.value.year,
      getParcelListFilter()
    );
    return response.data;
  };

  const loadWizardList = async (parcelId: string, year: number) => {
    const result = await statisticsApi.getStatisticsWizard({
      parcelId,
      source: DecisionSupportSource.DECISION_SUPPORT,
      year
    });
    return result.data;
  };

  const loadDecisionSupportById = async (id: string) => {
    const response = await statisticsApi.getStatisticsById(id);
    return response.data as any as DecisionSupportWizardData;
  };

  const nextStep = async () => {
    if (!isLastStep.value && isCurrentStepValid.value) {
      const nextStepIndex = currentStep.value + 1;
      const nextStep = steps.value[nextStepIndex - 1];

      if (currentStep.value === 2) {
        addParcelsMetadata();
      }
      if (nextStep.onLoad) {
        isNextStepLoading.value = true;
        await nextStep.onLoad();
        isNextStepLoading.value = false;
      }

      currentStep.value = nextStepIndex;
    }
  };

  const previousStep = async () => {
    if (!isFirstStep.value) {
      const prevStepIndex = currentStep.value - 1;
      const prevStep = steps.value[prevStepIndex - 1];

      if (prevStep.onLoad) {
        isNextStepLoading.value = true;
        await prevStep.onLoad();
        isNextStepLoading.value = false;
      }

      currentStep.value = prevStepIndex;
    } else {
      router.push({ name: 'decisionSupport' }).then();
      resetWizard();
    }
  };

  const addParcelsMetadata = () => {
    decisionSupportData.value.metadata.parcels = [];

    decisionSupportData.value.parcels.forEach((parcel) => {
      const { getChild } = useFormElement(parcel.form.element);

      const sourceField = getChild('source') as SelectableFormElement;
      const upkeepCostField = getChild('upkeepCost') as SelectableFormElement;
      const cropPriceField = getChild('cropPrice') as SelectableFormElement;

      const { value: sourceValue } = useSelectableFormElement(sourceField);
      const { value: upkeepCostValue } = useFormElement(upkeepCostField);
      const { value: cropPriceValue } = useFormElement(cropPriceField);

      const metadataParcelData = {
        id: parcel.id,
        upkeepCost: upkeepCostValue.value,
        cropPrice: cropPriceValue.value,
        layerGroupId: sourceValue.value.layerGroupId,
        layerGroupType: sourceValue.value.layerGroupType,
        layerGroupName: sourceValue.value.layerGroupName
      };

      decisionSupportData.value.metadata.parcels.push(metadataParcelData);
    });
  };

  const onDecisionSupportYearChange = async (year: number) => {
    decisionSupportData.value = {
      ...decisionSupportData.value,
      year,
      name: undefined,
      kind: StatisticsKind.DECISION_SUPPORT,
      parcels: [],
      metadata: {
        crop: decisionSupportData.value.metadata.crop,
        parcels: []
      }
    };
    clearLayers();
  };

  const onDecisionSupportCropChange = async (crop: string) => {
    decisionSupportData.value = {
      ...decisionSupportData.value,
      name: undefined,
      kind: StatisticsKind.DECISION_SUPPORT,
      parcels: [],
      metadata: {
        crop: crop,
        parcels: []
      }
    };
    clearLayers();
  };

  const openParcelSelectorModal = async () => {
    try {
      isParcelsLoading.value = true;
      const offers = await loadOffers();
      const parcelGroups = await loadParcelGroups();
      modals.open<ParcelGroupModalConfig>(ParcelGroupModal, {
        title: 'decisionSupport.modal.openParcelsTitle',
        confirmBtnLabel: 'button.open',
        text: '',
        width: '1000px',
        contentHeight: '500px',
        persistent: true,
        scrollable: true,
        translate: true,
        hideGroupForm: true,
        offers: offers,
        parcelGroups: parcelGroups,
        actualCropName: decisionSupportData.value.metadata.crop
          ? getCropNameByCode(decisionSupportData.value.metadata.crop)
          : undefined,
        onConfirm: async (parcels) => {
          await addSelectedParcels(parcels);
          await addLayers();
        },
        onCancel: () => {
          isParcelsLoading.value = false;
        }
      });
    } catch {
      notifications.showError('decisionSupport.wizard.messages.loadOffersError');
      isParcelsLoading.value = false;
    }
  };

  const addLayers = async () => {
    if (!decisionSupportData.value.parcels.length) {
      return;
    }

    const parcelIds = decisionSupportData.value.parcels
      .map((p) => p.id)
      .filter((p) => !layers.value.some((l) => l.id === p));
    const response = await parcelApi.getParcelsOutline(parcelIds);

    const features: any[] = response.data.features;

    for (const feature of features) {
      const mapLayerGroup = createVectorMapLayerGroup({
        id: feature.id,
        name: feature.properties.name,
        mapLayerConfigs: [
          {
            id: feature.id,
            classification: '',
            type: feature.type,
            name: '',
            featureProjection: MAP_PROJECTION_3857,
            dataProjection: MAP_PROJECTION_4326,
            rawData: feature
          }
        ]
      });

      addLayer(mapLayerGroup);
    }
  };

  const addSelectedParcels = async (parcels: ParcelItem[]) => {
    try {
      for (const parcel of parcels) {
        await setupSelectedParcel(parcel);
      }
      fitParcelsExtent();
      isParcelsLoading.value = false;
    } catch (e) {
      notifications.showError('decisionSupport.wizard.messages.openParcelsError');
      isParcelsLoading.value = false;
    }
  };

  const setupSelectedParcel = async (parcel: ParcelItem) => {
    const alreadyAdded = !!decisionSupportData.value.parcels.find((p) => p.id === parcel.id);

    const currentCrop = isCopy.value ? decisionSupportData.value.metadata.crop : parcel.currentCrop;

    if (alreadyAdded || !currentCrop) return;

    const decisionSupportWizardParcel: DecisionSupportWizardParcelData = {
      id: parcel.id,
      name: parcel.name,
      area: parcel.area || 0,
      svg: parcel.svg,
      mapData: null,
      blocked: parcel.blocked,
      bbox: transformExtent(parcel.bbox ?? [], MAP_PROJECTION_4326, MAP_PROJECTION_3857) as [
        number,
        number,
        number,
        number
      ],
      currentCrop: currentCrop,
      form: createForm(DECISION_SUPPORT_WIZARD_PARCEL_FORM_CONFIG)
    };

    decisionSupportData.value.parcels.push(decisionSupportWizardParcel);
  };

  const fitParcelsExtent = () => {
    const extent = decisionSupportData.value.parcels.reduce((prev: Extent, cur) => mergeExtents(prev, cur.bbox), []);
    zoomToExtent(extent);
  };

  const removeParcel = (parcelId: string) => {
    deleteParcelMetadata(parcelId);
    const index = decisionSupportData.value.parcels.findIndex((p) => p.id === parcelId);

    if (index >= 0) {
      decisionSupportData.value.parcels.splice(index, 1);
    }
    removeLayer(parcelId);
    fitParcelsExtent();
  };

  const deleteParcelMetadata = (parcelId: string) => {
    const parcelIndex = decisionSupportData.value.metadata.parcels.findIndex((p) => p.id === parcelId);
    if (parcelIndex !== -1) {
      decisionSupportData.value.metadata.parcels.splice(parcelIndex, 1);
    }
  };

  const saveDecisionSupport = async () => {
    if (!isSaveValid.value) return;
    isSaving.value = true;

    const decisionSupportDataToSave: SaveDecisionSupportWizardData = {
      name: decisionSupportData.value.name!,
      year: decisionSupportData.value.year!,
      kind: decisionSupportData.value.kind!,
      parcels: decisionSupportData.value.metadata.parcels.map((mp) => ({
        parcelId: mp.id,
        layerGroupId: mp.layerGroupId,
        layerGroupKind: mp.layerGroupType
      })),
      metadata: {
        crop: decisionSupportData.value.metadata.crop,
        parcels: decisionSupportData.value.metadata.parcels.map((mp) => ({
          id: mp.id,
          upkeepCost: mp.upkeepCost,
          cropPrice: mp.cropPrice,
          layerGroupId: mp.layerGroupId,
          layerGroupType: mp.layerGroupType,
          layerGroupName: mp.layerGroupName
        }))
      }
    };

    try {
      await decisionSupportApi.createDecisionSupport(decisionSupportDataToSave);
      notifications.showSuccess('decisionSupport.wizard.messages.saveDecisionSupportSuccess');
      await router.push({ name: 'decisionSupport' });
      isSaving.value = false;
      resetWizard();
    } catch {
      notifications.showError('decisionSupport.wizard.messages.saveDecisionSupportError');
      isSaving.value = false;
    }
  };

  const validateDecisionSupportSave = () => {
    isSaveValid.value =
      !!decisionSupportData.value.year &&
      !!decisionSupportData.value.name &&
      decisionSupportData.value.parcels.length > 0 &&
      decisionSupportData.value.metadata.parcels.length > 0 &&
      !!decisionSupportData.value.metadata.crop &&
      allParcelsValid.value;
  };

  const validateSourceStep = () => {
    return allParcelsValid.value && decisionSupportData.value.parcels.length > 0;
  };

  const validateStartStep = () => {
    return !!decisionSupportData.value.metadata.crop && !!decisionSupportData.value.year;
  };

  const onSourceStepLoad = async () => {
    nextTick(() => {
      openInfoModal();
      fitParcelsExtent();
    });
  };

  return {
    steps,
    currentStep,
    isFirstStep,
    isLastStep,
    isCurrentStepValid,
    isNextStepLoading,
    isParcelsLoading,
    isUiDisabled,
    isSaving,
    isCopy,
    needMetadataUpdate,
    map,
    isZoneScrollIntoViewDisabled,
    decisionSupportData,
    crops,
    isSaveValid,
    applyForm,
    getApplyForm,
    cropNameByCode,
    openInfoModal,
    applyUnkeepCost,
    applyCropPrice,
    copyDecisionSupport,
    getCropNameByCode,
    onDecisionSupportYearChange,
    onDecisionSupportCropChange,
    saveDecisionSupport,
    zoomToExtent,
    removeParcel,
    fitParcelsExtent,
    loadWizardList,
    nextStep,
    previousStep,
    openParcelSelectorModal,
    resetWizard,
    validateDecisionSupportSave
  };
});
